import { useEffect, useState } from 'react';

import { Drawer, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import ButtonCard from './DrawerElements/ButtonCard';
import DeviceCard from './DrawerElements/DeviceCard';
import ItemCard from './DrawerElements/ItemCard';
import LatestActivityCard from './DrawerElements/LatestActivityCard';
import DeviceLogo from '../../../assets/images/icons-ic-skopei.png';
import { LoadingBar } from '../../../components';
import { getDeviceTypeLabel, isObject, isArray, isNull } from '../../../shared/utility';
import * as actions from '../../../store/actions';
import { useMainContext } from '../FleetManagement';
import { useStyles } from '../style';

interface FleetDrawerProps {
	openFleetDrawer?: boolean;
	selectedFleet?: object;
	onFetchInstance?(...args: unknown[]): unknown;
	onResetState?(...args: unknown[]): unknown;
	onFetchItemImage?(...args: unknown[]): unknown;
	onFetchPlanboardItemsToAdd?(...args: unknown[]): unknown;
	selectedItem?: object;
	setSelectedItem?(...args: unknown[]): unknown;
	onFetchItemInstances?(...args: unknown[]): unknown;
	setSelectedUser?(...args: unknown[]): unknown;
	selectedUser?: object;
	onPlanboardBooking?(...args: unknown[]): unknown;
	setHasChangedItem?(...args: unknown[]): unknown;
	isUnavailability?: boolean;
	onFetchUnavailability?(...args: unknown[]): unknown;
	fetchItemImage?: {
		data?: unknown[];
		loading?: boolean;
		error?: object | string;
	};
	fetchInstance?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	fetchDeviceHeartbeats?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const FleetDrawer = (props: FleetDrawerProps) => {
	const {
		onFetchInstance,
		fetchInstance,
		onResetState,
		onFetchItemImage,
		fetchItemImage,
		fetchDeviceHeartbeats,
	} = props;
	const { t } = useTranslation();

	const { selectedFleet, openFleetDrawer, setSelectedFleet, setSelectedIndex } = useMainContext();

	const { data: fetchInstanceData, loading: fetchInstanceLoading } = fetchInstance;

	const { data: fetchItemImageData, loading: fetchItemImageLoading } = fetchItemImage;

	const { data: fetchDeviceHeartbeatsData } = fetchDeviceHeartbeats;

	const [cardData, setCardData] = useState(null);

	const [requestedResources] = useState(5);

	const classes = useStyles();

	useEffect(() => {
		if (selectedFleet?.itemId && !fetchInstanceLoading) {
			onFetchInstance(selectedFleet.itemId, selectedFleet.instanceId);
			onFetchItemImage(selectedFleet?.itemId);
		}
	}, [selectedFleet]);

	useEffect(() => {
		const description = `${t('ui.label.firmwareVersion')} ${fetchDeviceHeartbeatsData?.firmwareVersion ? fetchDeviceHeartbeatsData.firmwareVersion : '-'}`;
		if (selectedFleet?.type === 'devices') {
			setCardData({
				name: `${selectedFleet.deviceId}`,
				subName: getDeviceTypeLabel(selectedFleet.deviceType),
				description: description,
				image: DeviceLogo,
				type: selectedFleet?.type,
			});
		} else if (
			isObject(fetchInstanceData) &&
			!fetchItemImageLoading &&
			isArray(fetchItemImageData)
		) {
			setCardData({
				name: fetchInstanceData.itemReference.name,
				subName: fetchInstanceData.name,
				description: description,
				image: fetchItemImageData[0],
			});
		}
	}, [selectedFleet, fetchInstanceData, fetchItemImageData]);

	useEffect(() => {
		if (!openFleetDrawer) {
			onResetState('fetchInstance');
			setCardData(null);
			onResetState('fetchItemImage');
			setSelectedFleet(null);
			setSelectedIndex(null);
		}
	}, [openFleetDrawer]);

	const handleLink = () =>
		cardData?.subName && selectedFleet?.itemId && selectedFleet?.instanceId ?
			<Link
				className={classes.itemGroup}
				color='inherit'
				component={RouterLink}
				to={`/item-management/${selectedFleet?.itemId}/instance/${selectedFleet?.instanceId}/summary`}
				state={{ from: location.pathname }}
			>
				{cardData.subName}
			</Link>
		:	<Typography>
				{isNull(cardData) ?
					<LoadingBar />
				:	cardData.subName}
			</Typography>;

	const handleLinkDevice = () =>
		cardData?.subName && selectedFleet ?
			<Link
				className={classes.deviceCardItems}
				color='inherit'
				component={RouterLink}
				to={`/devices/${selectedFleet?.deviceId}/summary`}
				state={{ from: location.pathname }}
			>
				{`${selectedFleet?.deviceId}`}
			</Link>
		:	null;

	const handleCardNameLink = () => (
		<Link
			color='inherit'
			component={RouterLink}
			to={{
				pathname:
					cardData.type === 'devices' ?
						`/devices/${selectedFleet?.deviceId}/summary`
					:	`/item-management/items/${selectedFleet?.itemId}/summary`,
			}}
			state={{ from: location.pathname }}
		>
			{cardData.type === 'devices' ? `${selectedFleet?.deviceId}` : cardData.name}
		</Link>
	);

	const deviceProps = {
		openFleetDrawer,
		selectedFleet,
		cardData,
		setCardData,
		handleLinkDevice,
	};

	const latestActivityProps = {
		itemInstanceId: selectedFleet?.instanceId,
		requestedResources: requestedResources,
		selectedFleet,
	};

	const drawerProps = {
		anchor: 'right',
		classes: { paper: classes.drawer },
		open: openFleetDrawer,
		hideBackdrop: true,
		variant: 'persistent',
	};

	return (
		<Drawer {...drawerProps}>
			<form>
				<ButtonCard />
				{/* <Canvas image={fetchItemImageData && fetchItemImageData[0]} width={100} height={100} /> */}
				<ItemCard
					cardData={cardData}
					handleCardNameLink={handleCardNameLink}
					handleLink={handleLink}
				/>
				{selectedFleet?.deviceId ?
					<DeviceCard {...deviceProps} />
				:	null}
				<LatestActivityCard {...latestActivityProps} />
			</form>
		</Drawer>
	);
};

const mapStateToProps = (state) => {
	return {
		fetchInstance: state.details.fetchInstance,
		fetchItemImage: state.details.fetchItemImage,
		planboardBooking: state.details.planboardBooking,
		planboardItemsToAdd: state.planboard.planboardItemsToAdd,
		itemInstancesList: state.paged.itemInstances,
		fetchUnavailability: state.details.fetchUnavailability,
		fetchDeviceHeartbeats: state.details.fetchDeviceHeartbeats,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchInstance: (itemId, instanceId) => dispatch(actions.fetchInstance(itemId, instanceId)),
		onResetState: (state) => dispatch(actions.resetState(state)),
		onFetchItemImage: (itemId) => dispatch(actions.fetchItemImage(itemId)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(FleetDrawer);
